import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Activity, BarChart3, Calendar, Filter, Flame, ListTree, TrendingUp, Trophy, type LucideIcon } from 'lucide-react';
import { GuidedEmpty } from '../components/GuidedEmpty';
import { ModuleHero } from '../components/ModuleHero';
import { useStore } from '../hooks/useStore';
import { useActiveTournament } from '../state/TournamentContext';
import { getBracketSeeds, getStandings, recomputeStandings } from '../services/scoringService';
import { listAnnouncements } from '../services/communicationHubService';
import { computePlayerBadges, computeTeamBadges } from '../services/gamificationService';
import { buildIcs, downloadIcs } from '../services/calendarExport';
import { buildPublicEventSnapshot, type PublicEventScheduleRow, type PublicEventSnapshot } from '../services/liveGameService';
import type { Division, StandingsRecord } from '../models';

interface LiveEventResponse {
  ok?: boolean;
  snapshot?: PublicEventSnapshot;
}

interface ScheduleDateGroup {
  key: string;
  label: string;
  divisionGroups: Array<{
    key: string;
    label: string;
    rows: PublicEventScheduleRow[];
  }>;
}

export function StandsPage() {
  const db = useStore((d) => d);
  const { tournament, divisions, teams, games } = useActiveTournament();
  const [filterDivision, setFilterDivision] = useState<string>('');
  const [remoteSnapshot, setRemoteSnapshot] = useState<PublicEventSnapshot | null>(null);

  const localSnapshot = useMemo(() => buildPublicEventSnapshot(db, tournament.id), [db, tournament.id]);
  const eventSnapshot = remoteSnapshot?.tournament.id === tournament.id ? remoteSnapshot : localSnapshot;

  useEffect(() => {
    if (getStandings(tournament.id).length === 0) {
      recomputeStandings(tournament.id);
    }
  }, [tournament.id]);

  useEffect(() => {
    if (!tournament.slug) return;
    let cancelled = false;
    let timer: number | null = null;
    const load = async () => {
      try {
        const response = await fetch(`/api/live/events/${encodeURIComponent(tournament.slug)}`);
        const data = (await response.json()) as LiveEventResponse;
        if (!cancelled && data.ok && data.snapshot) setRemoteSnapshot(data.snapshot);
      } catch {
        // Local store snapshot remains the fallback in dev/test.
      } finally {
        const interval = (eventSnapshot?.liveGames.length ?? 0) > 0 ? 5000 : 30000;
        if (!cancelled) timer = window.setTimeout(load, interval);
      }
    };
    void load();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [tournament.slug, eventSnapshot?.liveGames.length]);

  const standings = useMemo(() => {
    return eventSnapshot?.standings ?? getStandings(tournament.id);
  }, [eventSnapshot?.standings, tournament.id, db.standings]);

  const filteredStandings = standings.filter((row) => !filterDivision || row.divisionId === filterDivision);
  const standingsByDivision = groupStandingsByDivision(filteredStandings, divisions);
  const scheduleRows = (eventSnapshot?.schedule ?? []).filter((row) => !filterDivision || row.divisionId === filterDivision);
  const scheduleGroups = groupScheduleRows(scheduleRows);
  const upcomingRows = scheduleRows.filter((row) => row.status === 'scheduled' || row.status === 'ready').slice(0, 6);
  const finalRows = scheduleRows
    .filter((row) => row.status === 'final' || row.status === 'forfeit')
    .sort((a, b) => (b.result?.endedAt ?? b.startsAt ?? '').localeCompare(a.result?.endedAt ?? a.startsAt ?? ''))
    .slice(0, 6);
  const liveSnapshots = (eventSnapshot?.liveGames ?? []).filter((snapshot) => !filterDivision || snapshot.game.divisionId === filterDivision);
  const announcements = listAnnouncements(tournament.id).slice(0, 3);
  const playerStats = (eventSnapshot?.playerLeaders.points ?? []).slice(0, 5);
  const bracketSeeds = getBracketSeeds(tournament.id);
  const playerBadges = computePlayerBadges(tournament.id);
  const teamBadges = computeTeamBadges(tournament.id);
  const totals = eventSnapshot?.analytics.totals;
  const teamById = new Map(teams.map((team) => [team.id, team]));

  return (
    <>
      <ModuleHero
        eyebrow={tournament.format === 'season' ? 'Season game center' : 'Tournament game center'}
        title={`${tournament.name} game center.`}
        description="Live games, full schedule, standings, leaders, and finals from official scorekeeper data."
        icon={Trophy}
        accent="goal"
        status={
          liveSnapshots.length > 0
            ? { label: `${liveSnapshots.length} live game${liveSnapshots.length === 1 ? '' : 's'}`, tone: 'ok' }
            : { label: 'Between games', tone: 'info' }
        }
      >
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <Filter size={14} color="var(--text-muted)" style={{ alignSelf: 'center' }} />
          <select className="select" value={filterDivision} onChange={(e) => setFilterDivision(e.target.value)} style={{ maxWidth: 220 }}>
            <option value="">All divisions</option>
            {divisions.map((division) => <option key={division.id} value={division.id}>{division.name}</option>)}
          </select>
          <button
            className="btn btn--ghost btn--sm"
            onClick={() => {
              const ics = buildIcs({
                tournament,
                games,
                slots: db.scheduleSlots.filter((slot) => slot.tournamentId === tournament.id),
                teams,
                rinks: db.rinks,
                venues: db.venues,
              });
              downloadIcs(`${tournament.slug || 'schedule'}.ics`, ics);
            }}
          >
            <Calendar size={14} /> Add to calendar
          </button>
        </div>
      </ModuleHero>

      {totals && (
        <section className="public-event__kpis">
          <PublicEventKpi label="Live" value={totals.liveGames} detail="Games in progress" />
          <PublicEventKpi label="Finals" value={totals.finalGames} detail="Official results" />
          <PublicEventKpi label="Teams" value={teams.length} detail={`${divisions.length} division${divisions.length === 1 ? '' : 's'}`} />
          <PublicEventKpi label="Goals" value={totals.totalGoals} detail="Across final games" />
        </section>
      )}

      {announcements.length > 0 && (
        <div className="card card--accent">
          <div className="card__head"><h3 className="card__title">Announcements</h3></div>
          <div className="stack">
            {announcements.map((announcement) => (
              <div key={announcement.id}>
                <strong>{announcement.title}</strong>
                <p className="muted" style={{ marginTop: 2 }}>{announcement.body}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {eventSnapshot?.analytics && <EventIntelligence snapshot={eventSnapshot} />}

      <section className="card">
        <div className="card__head">
          <div>
            <h3 className="card__title">Live now</h3>
            <p className="card__sub">Active games update automatically from the live snapshot.</p>
          </div>
        </div>
        {liveSnapshots.length === 0 ? (
          <GuidedEmpty icon={Activity} title="No games live right now" body="Live games move here as scorekeepers start them." />
        ) : (
          <div className="grid grid--2">
            {liveSnapshots.map((snapshot) => (
              <div key={snapshot.game.id} className="card card--accent">
                <div className="row row--between">
                  <span className="chip chip--ok"><span className="chip__dot" /> Live</span>
                  <span className="chip">{snapshot.divisionName ?? ''}</span>
                </div>
                <Link to={`/events/${tournament.slug}/games/${snapshot.game.id}`} className="public-game-card-link">
                  <div className="row row--between" style={{ marginTop: 16, alignItems: 'center' }}>
                    <TeamScore name={snapshot.homeTeam.name} color={snapshot.homeTeam.primaryColor} score={snapshot.score.home} />
                    <div className="h-display" style={{ fontSize: 24, color: 'var(--text-muted)' }}>vs</div>
                    <TeamScore name={snapshot.awayTeam.name} color={snapshot.awayTeam.primaryColor} score={snapshot.score.away} reverse />
                  </div>
                </Link>
              </div>
            ))}
          </div>
        )}
      </section>

      <div className="grid grid--2">
        <GameListCard
          title="Up next"
          subtitle="Next scheduled games."
          emptyTitle="No upcoming games"
          emptyBody="Generate a schedule in League HQ to show players what is next."
          icon={Calendar}
          rows={upcomingRows}
          slug={tournament.slug}
        />
        <GameListCard
          title="Recent finals"
          subtitle="Latest official results."
          emptyTitle="No completed games yet"
          emptyBody="Final scores appear here after scorekeepers submit games."
          icon={Trophy}
          rows={finalRows}
          slug={tournament.slug}
        />
      </div>

      <section className="card">
        <div className="card__head">
          <div>
            <h3 className="card__title">Standings</h3>
            <p className="card__sub">Grouped by division and recomputed from official final scores.</p>
          </div>
        </div>
        {standingsByDivision.length === 0 ? (
          <GuidedEmpty icon={ListTree} title="No standings yet" body="Standings fill in automatically after the first final score." />
        ) : (
          <div className="public-event__standings-grid">
            {standingsByDivision.map((group) => (
              <div key={group.key} className="public-event__standings-card">
                <div className="eyebrow">{group.label}</div>
                <div className="table-scroll">
                  <table className="table public-event__standings-table">
                    <thead>
                      <tr><th>#</th><th>Team</th><th>GP</th><th>W</th><th>L</th><th>T</th><th>GF</th><th>GA</th><th>+/-</th><th>PIM</th><th>Pts</th></tr>
                    </thead>
                    <tbody>
                      {group.rows.map((standing) => {
                        const team = teamById.get(standing.teamId);
                        return (
                          <tr key={standing.id}>
                            <td className="mono">{standing.rank ?? '-'}</td>
                            <td>
                              <Link to={`/events/${tournament.slug}/teams/${standing.teamId}`} className="public-game-list-link public-event__team-cell">
                                <span style={{ background: team?.primaryColor ?? '#888' }} />
                                <strong>{team?.name ?? '-'}</strong>
                              </Link>
                            </td>
                            <td>{standing.gamesPlayed}</td>
                            <td>{standing.wins}</td>
                            <td>{standing.losses}</td>
                            <td>{standing.ties}</td>
                            <td>{standing.goalsFor}</td>
                            <td>{standing.goalsAgainst}</td>
                            <td>{standing.goalsFor - standing.goalsAgainst}</td>
                            <td>{standing.penaltyMinutes ?? 0}</td>
                            <td className="mono">{standing.points ?? 0}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="card">
        <div className="card__head">
          <div>
            <h3 className="card__title">Full schedule</h3>
            <p className="card__sub">{scheduleRows.length} game{scheduleRows.length === 1 ? '' : 's'} grouped by date and division.</p>
          </div>
        </div>
        {scheduleGroups.length === 0 ? (
          <GuidedEmpty icon={Calendar} title="No scheduled games" body="Generate a schedule to publish the public game list." />
        ) : (
          <div className="public-event__schedule">
            {scheduleGroups.map((dateGroup) => (
              <div key={dateGroup.key} className="public-event__schedule-day">
                <h4>{dateGroup.label}</h4>
                {dateGroup.divisionGroups.map((divisionGroup) => (
                  <div key={`${dateGroup.key}-${divisionGroup.key}`} className="public-event__schedule-division">
                    <div className="eyebrow">{divisionGroup.label}</div>
                    <div className="public-event__schedule-list">
                      {divisionGroup.rows.map((row) => (
                        <ScheduleRow key={row.gameId} row={row} slug={tournament.slug} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </section>

      <div className="grid grid--2">
        <div className="card">
          <div className="card__head">
            <div>
              <h3 className="card__title">
                <Flame size={16} style={{ verticalAlign: -2, marginRight: 6 }} />
                Hot players
              </h3>
              <p className="card__sub">Top 5 by points.</p>
            </div>
          </div>
          {playerStats.length === 0 ? (
            <GuidedEmpty icon={Flame} title="No player stats yet" body="Goals, assists, shots, and penalty minutes appear here after scorer-table events are recorded." />
          ) : (
            <table className="table">
              <thead><tr><th>Player</th><th>G</th><th>A</th><th>Pts</th></tr></thead>
              <tbody>
                {playerStats.map((stat) => (
                  <tr key={stat.playerId}>
                    <td>
                      <Link to={`/events/${tournament.slug}/players/${stat.playerId}`}><strong>{stat.playerName}</strong></Link>
                      <div className="muted" style={{ fontSize: 12 }}>
                        {stat.teamId ? <Link to={`/events/${tournament.slug}/teams/${stat.teamId}`}>{stat.teamName ?? ''}</Link> : stat.teamName ?? ''}
                      </div>
                    </td>
                    <td>{stat.goals}</td>
                    <td>{stat.assists}</td>
                    <td className="mono">{stat.points}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="card">
          <div className="card__head">
            <div>
              <h3 className="card__title">
                <ListTree size={16} style={{ verticalAlign: -2, marginRight: 6 }} />
                Bracket seeding
              </h3>
              <p className="card__sub">Current standings projected to playoff seeds.</p>
            </div>
          </div>
          {bracketSeeds.length === 0 ? (
            <GuidedEmpty icon={ListTree} title="No playoff seeds yet" body="Seeds are projected from standings once teams and final scores exist." />
          ) : (
            <div className="stack">
              {divisions.map((division) => {
                const seeds = bracketSeeds.filter((seed) => seed.divisionId === division.id);
                if (seeds.length === 0) return null;
                return (
                  <div key={division.id}>
                    <div className="eyebrow">{division.name}</div>
                    <div className="stack" style={{ marginTop: 4 }}>
                      {seeds.map((seed) => {
                        const team = teamById.get(seed.teamId);
                        return (
                          <div key={seed.teamId} className="row row--between" style={{ padding: '4px 0' }}>
                            <Link to={`/events/${tournament.slug}/teams/${seed.teamId}`}><strong>#{seed.seed}</strong> {team?.name ?? '-'}</Link>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {(playerBadges.length > 0 || teamBadges.length > 0) && (
        <div className="card">
          <div className="card__head">
            <h3 className="card__title">Badges & moments</h3>
            <p className="card__sub">Earned automatically from live results.</p>
          </div>
          <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
            {teamBadges.map((badge, index) => {
              const team = teamById.get(badge.teamId);
              return (
                <Link key={`tb-${index}`} to={`/events/${tournament.slug}/teams/${badge.teamId}`} className="chip chip--accent">
                  {badge.label} - <strong>{team?.name ?? '-'}</strong>
                </Link>
              );
            })}
            {playerBadges.map((badge, index) => {
              const player = db.players.find((row) => row.id === badge.playerId);
              const profile = player ? db.profiles.find((row) => row.id === player.profileId) : null;
              return (
                <Link key={`pb-${index}`} to={`/events/${tournament.slug}/players/${badge.playerId}`} className="chip chip--ok">
                  {badge.label} - <strong>{profile?.displayName ?? '-'}</strong>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}

function PublicEventKpi({ label, value, detail }: { label: string; value: string | number; detail: string }) {
  return (
    <div className="public-event__kpi">
      <span>{label}</span>
      <strong>{value}</strong>
      <p>{detail}</p>
    </div>
  );
}

function GameListCard({
  title,
  subtitle,
  emptyTitle,
  emptyBody,
  icon,
  rows,
  slug,
}: {
  title: string;
  subtitle: string;
  emptyTitle: string;
  emptyBody: string;
  icon: LucideIcon;
  rows: PublicEventScheduleRow[];
  slug: string;
}) {
  return (
    <div className="card">
      <div className="card__head">
        <div>
          <h3 className="card__title">{title}</h3>
          <p className="card__sub">{subtitle}</p>
        </div>
      </div>
      {rows.length === 0 ? (
        <GuidedEmpty icon={icon} title={emptyTitle} body={emptyBody} />
      ) : (
        <div className="stack">
          {rows.map((row) => (
            <Link key={row.gameId} to={`/events/${slug}/games/${row.gameId}`} className="public-event__game-row">
              <div>
                <strong>{row.homeTeam.name}</strong> vs <strong>{row.awayTeam.name}</strong>
                <div className="muted" style={{ fontSize: 12 }}>{formatGameTime(row.startsAt)}{row.rinkName ? ` - ${row.rinkName}` : ''}</div>
              </div>
              <div className="public-event__game-row-side">
                <span className={`chip ${statusChipClass(row)}`}>{formatStatus(row)}</span>
                {(row.result || row.status === 'in_progress') && <strong className="mono">{formatScore(row)}</strong>}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function ScheduleRow({ row, slug }: { row: PublicEventScheduleRow; slug: string }) {
  return (
    <Link to={`/events/${slug}/games/${row.gameId}`} className="public-event__schedule-row">
      <div className="public-event__schedule-time">
        <strong>{formatTimeOnly(row.startsAt)}</strong>
        <span>{row.rinkName ?? 'Rink TBD'}</span>
      </div>
      <div className="public-event__schedule-matchup">
        <strong>{row.homeTeam.name}</strong>
        <span>vs</span>
        <strong>{row.awayTeam.name}</strong>
      </div>
      <div className="public-event__schedule-result">
        <span className={`chip ${statusChipClass(row)}`}>{formatStatus(row)}</span>
        <strong className="mono">{row.result || row.status === 'in_progress' ? formatScore(row) : '-'}</strong>
      </div>
    </Link>
  );
}

function TeamScore({ name, color, score, reverse }: { name: string; color: string; score: number; reverse?: boolean }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, flexDirection: reverse ? 'row-reverse' : 'row' }}>
      <div style={{ width: 10, height: 38, borderRadius: 999, background: color }} />
      <div style={{ textAlign: reverse ? 'right' : 'left' }}>
        <div style={{ fontWeight: 700 }}>{name}</div>
        <div className="h-display" style={{ fontSize: 36, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{score}</div>
      </div>
    </div>
  );
}

function EventIntelligence({ snapshot }: { snapshot: PublicEventSnapshot }) {
  const title = snapshot.tournament.format === 'season' ? 'Season intelligence' : 'Event intelligence';
  const teamRows = snapshot.analytics.teamPulse.slice(0, 4);
  return (
    <section className="card public-event-intel">
      <div className="card__head">
        <div>
          <h3 className="card__title">
            <Activity size={16} style={{ verticalAlign: -2, marginRight: 6 }} />
            {title}
          </h3>
          <p className="card__sub">A live read on scoring pace, standings pressure, and team form.</p>
        </div>
        <span className="chip chip--accent">{snapshot.analytics.totals.totalGames} game{snapshot.analytics.totals.totalGames === 1 ? '' : 's'}</span>
      </div>

      <div className="public-event-intel__metrics">
        {snapshot.analytics.metrics.map((metric) => (
          <div key={metric.label} className="public-event-intel__metric">
            <span>{metric.label}</span>
            <strong>{metric.value}</strong>
            <p>{metric.detail}</p>
          </div>
        ))}
      </div>

      <div className="public-event-intel__grid">
        <div>
          <div className="eyebrow" style={{ marginBottom: 10 }}>
            <BarChart3 size={13} style={{ verticalAlign: -2, marginRight: 5 }} />
            Key reads
          </div>
          <div className="public-event-intel__insights">
            {snapshot.analytics.keyInsights.map((insight) => (
              <div key={`${insight.label}-${insight.value}`} className={`public-event-intel__insight public-event-intel__insight--${insight.tone}`}>
                <span>{insight.label}</span>
                <strong>{insight.value}</strong>
                <p>{insight.detail}</p>
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="eyebrow" style={{ marginBottom: 10 }}>
            <TrendingUp size={13} style={{ verticalAlign: -2, marginRight: 5 }} />
            Team pulse
          </div>
          {teamRows.length === 0 ? (
            <div className="public-event-intel__empty">Team trends appear after standings are available.</div>
          ) : (
            <div className="public-event-intel__teams">
              {teamRows.map((team) => (
                <Link key={team.teamId} to={`/events/${snapshot.tournament.slug}/teams/${team.teamId}`} className="public-event-intel__team">
                  <span className="public-event-intel__team-color" style={{ background: team.primaryColor }} />
                  <div>
                    <strong>{team.teamName}</strong>
                    <p>{team.trendLabel} - {team.points} pts - {team.goalsFor}-{team.goalsAgainst}</p>
                  </div>
                  <div className="public-event-intel__form" aria-label={`${team.teamName} recent form`}>
                    {team.form.length === 0 ? (
                      <span className="public-event-intel__form-empty">-</span>
                    ) : team.form.map((result, index) => (
                      <span key={`${team.teamId}-${result}-${index}`} className={`public-event-intel__form-pill public-event-intel__form-pill--${result.toLowerCase()}`}>
                        {result}
                      </span>
                    ))}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function groupStandingsByDivision(standings: StandingsRecord[], divisions: Division[]) {
  const divisionById = new Map(divisions.map((division) => [division.id, division]));
  const groups = new Map<string, { key: string; label: string; rows: StandingsRecord[] }>();
  for (const standing of standings) {
    const key = standing.divisionId ?? 'all';
    if (!groups.has(key)) {
      groups.set(key, { key, label: divisionById.get(key)?.name ?? 'Division', rows: [] });
    }
    groups.get(key)!.rows.push(standing);
  }
  return Array.from(groups.values()).map((group) => ({
    ...group,
    rows: [...group.rows].sort((a, b) => (a.rank ?? 999) - (b.rank ?? 999)),
  }));
}

function groupScheduleRows(rows: PublicEventScheduleRow[]): ScheduleDateGroup[] {
  const dateGroups = new Map<string, ScheduleDateGroup>();
  for (const row of rows) {
    const key = dateKey(row.startsAt);
    if (!dateGroups.has(key)) {
      dateGroups.set(key, { key, label: formatDateLabel(row.startsAt), divisionGroups: [] });
    }
    const dateGroup = dateGroups.get(key)!;
    const divisionKey = row.divisionId ?? 'all';
    let divisionGroup = dateGroup.divisionGroups.find((group) => group.key === divisionKey);
    if (!divisionGroup) {
      divisionGroup = { key: divisionKey, label: row.divisionName ?? 'Division', rows: [] };
      dateGroup.divisionGroups.push(divisionGroup);
    }
    divisionGroup.rows.push(row);
  }
  return Array.from(dateGroups.values()).map((dateGroup) => ({
    ...dateGroup,
    divisionGroups: dateGroup.divisionGroups
      .map((divisionGroup) => ({
        ...divisionGroup,
        rows: [...divisionGroup.rows].sort((a, b) => (a.startsAt ?? '').localeCompare(b.startsAt ?? '')),
      }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  }));
}

function dateKey(value?: string): string {
  if (!value) return 'tbd';
  return new Date(value).toISOString().slice(0, 10);
}

function formatDateLabel(value?: string): string {
  if (!value) return 'Time TBD';
  return new Date(value).toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' });
}

function formatGameTime(value?: string): string {
  if (!value) return 'Time TBD';
  return new Date(value).toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function formatTimeOnly(value?: string): string {
  if (!value) return 'TBD';
  return new Date(value).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function formatStatus(row: PublicEventScheduleRow): string {
  if (row.status === 'in_progress') return 'Live';
  if (row.status === 'ready') return 'Ready';
  if (row.status === 'forfeit') return 'Forfeit';
  if (row.status === 'final') {
    if (row.result?.decidedBy === 'shootout') return 'SO final';
    if (row.result?.decidedBy === 'overtime') return 'OT final';
    return 'Final';
  }
  return row.status.replace('_', ' ');
}

function statusChipClass(row: PublicEventScheduleRow): string {
  if (row.status === 'in_progress') return 'chip--ok';
  if (row.status === 'final') return 'chip--accent';
  if (row.status === 'forfeit') return 'chip--warn';
  return '';
}

function formatScore(row: PublicEventScheduleRow): string {
  const home = row.result?.homeScore ?? row.score.home;
  const away = row.result?.awayScore ?? row.score.away;
  return `${home}-${away}`;
}
